/* eslint-disable camelcase */
import { mobileMediaQuery } from '@caterdesk/ui--theme'
import { Typography, Box, Stack, DialogContent, styled as muiStyled } from '@mui/material'
import { PieChart, useDrawingArea } from '@mui/x-charts'

import type { TFunction } from 'i18next'
import type React from 'react'
import { useTranslation } from 'react-i18next'
import { useMedia } from 'react-use'
import { match, P } from 'ts-pattern'

import type { MenuItem } from '../../domain/menu-item'
import type { MenuVendor } from '../../domain/menu-vendor'

type Props = {
  vendor: Pick<MenuVendor, 'companyName'>
  item: MenuItem
}

const StyledText = muiStyled('text')(({ theme }) => ({
  fill: theme.palette.text.primary,
  textAnchor: 'middle',
  dominantBaseline: 'central',
  fontSize: 20,
}))

function PieCenterLabel({ amount, t }: { amount: number; t: TFunction }) {
  const { width, height, left, top } = useDrawingArea()
  const centerX = left + width / 2
  const centerY = top + height / 2
  const lineSpacing = 25

  return (
    <StyledText x={centerX} y={centerY}>
      <tspan x={centerX} y={centerY - lineSpacing / 2}>
        {amount}
      </tspan>
      <tspan x={centerX} y={centerY + lineSpacing / 2}>
        {t('marketplace:calories')}
      </tspan>
    </StyledText>
  )
}

export const NutritionalTab: React.FC<Props> = ({ vendor, item }) => {
  const isMobileScreen = useMedia(mobileMediaQuery, false)
  const { t } = useTranslation()

  const {
    macros_kcal,
    macros_protein,
    macros_carbs,
    macros_fat,
    macros_sugar,
    macros_saturates,
    macros_fibre,
    macros_salt,
  } = item

  const hasNutritionalData = Boolean(
    macros_kcal ||
    macros_protein ||
    macros_carbs ||
    macros_fat ||
    macros_sugar ||
    macros_saturates ||
    macros_fibre ||
    macros_salt,
  )

  const macros = [
    {
      label: 'kcal',
      value: macros_kcal,
    },
    {
      label: 'protein',
      value: macros_protein,
    },
    {
      label: 'carbs',
      value: macros_carbs,
    },
    {
      label: 'fat',
      value: macros_fat,
    },
    {
      label: 'sugar',
      value: macros_sugar,
    },
    {
      label: 'sat. fat',
      value: macros_saturates,
    },
    {
      label: 'fibre',
      value: macros_fibre,
    },
    {
      label: 'salt',
      value: macros_salt,
    },
  ]

  return (
    <DialogContent>
      <Stack direction="column" spacing={2} alignItems="stretch">
        {match(hasNutritionalData)
          .with(true, () => (
            <>
              <Box>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5 }}>
                  {t('marketplace:nutritionalInformation')}
                </Typography>
                <Box sx={{ py: 0.5 }}>
                  <Typography variant="caption" sx={{ lineHeight: '24px', display: 'block' }}>
                    {t('cc:allInformationProvidedBy')} {vendor.companyName}
                  </Typography>
                  <Typography variant="caption" sx={{ lineHeight: '24px', display: 'block' }}>
                    {t('marketplace:dailyCalorieNote')}
                  </Typography>
                </Box>
              </Box>

              {match(item)
                .with(
                  {
                    macros_protein: P.number,
                    macros_carbs: P.number,
                    macros_fat: P.number,
                    macros_kcal: P.number,
                  },
                  ({ macros_protein, macros_carbs, macros_fat, macros_kcal }) => {
                    const pieChartData = [
                      {
                        value: macros_protein,
                        label: 'Protein',
                      },
                      {
                        value: macros_carbs,
                        label: 'Carbohydrates',
                      },
                      {
                        value: macros_fat,
                        label: 'Fat',
                      },
                    ]
                    return (
                      <Box>
                        <PieChart
                          series={[
                            {
                              data: pieChartData,
                              valueFormatter: (item: { value: number }) => `${item.value}g`,
                              innerRadius: 70,
                            },
                          ]}
                          height={200}>
                          <PieCenterLabel amount={macros_kcal} t={t} />
                        </PieChart>
                      </Box>
                    )
                  },
                )
                .otherwise(() => null)}

              <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', mb: 1 }}>
                {t('marketplace:baseDish')}{' '}
                <Typography
                  component="span"
                  sx={{ color: 'text.secondary', textTransform: 'capitalize' }}>
                  ({t('marketplace:serves', { servesAmount: 1 }).toLowerCase()})
                </Typography>
              </Typography>
              <Stack
                spacing={0.5}
                direction={isMobileScreen ? 'column' : 'row'}
                sx={{ flexWrap: 'wrap' }}>
                {macros.map((macro) =>
                  macro.value ? (
                    <Box
                      key={macro.label}
                      sx={{
                        width: '100%',
                        borderRadius: '5px',
                        padding: '8px 16px',
                        backgroundColor: 'grey.100',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexGrow: isMobileScreen ? 1 : 0,
                      }}>
                      <Stack direction="column" spacing={0.5} alignItems="center">
                        <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500 }}>
                          {match(macro)
                            .with({ label: 'kcal' }, (macro) => macro.value)
                            .with({ label: 'salt' }, (macro) => `${macro.value || ''}mg`)
                            .otherwise(() => `${macro.value || ''}g`)}
                        </Typography>
                        <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
                          {macro.label}
                        </Typography>
                      </Stack>
                    </Box>
                  ) : null,
                )}
              </Stack>
            </>
          ))
          .with(false, () => (
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5 }}>
                {t('marketplace:noNutritionalInfo')}
              </Typography>
              <Typography variant="caption" sx={{ lineHeight: '24px' }}>
                {t('marketplace:waitingOnInfoFrom')} {vendor.companyName}
              </Typography>
            </Box>
          ))
          .exhaustive()}
      </Stack>
    </DialogContent>
  )
}
